import slugify from 'slugify';
import { snakeToCamel } from './strings.util';
import { SelectFilterOptions } from '@components/SelectFilter';

const GALLERY_PRODUCT_TYPE_TO_SCENARIO_MAP: { [key in GalleryProductType]: SignUpScenario } = {
  Invitation: 'invitation',
  'Save the Date': 'save-the-date',
  'Wedding Website': 'wedding-website',
  Prints: 'prints',
} as const;

export const getSignUpScenario = (galleryProductType: GalleryProductType) => GALLERY_PRODUCT_TYPE_TO_SCENARIO_MAP[galleryProductType];

export const eCardCategorySlugsToTagsMap: { [key in string]: ReadonlyArray<GalleryProductTag> } = {
  bold: ['Bold', 'Geometric', 'Bright', 'Typographic'],
  classic: ['Formal', 'Elegant', 'Romantic', 'Classic', 'Art Deco'],
  cultural: ['Cultural', 'Religious', 'Indian', 'Indonesian', 'Chinese', 'Japanese', 'Mexican'],
  destination: ['Destination', 'Beach', 'Mountains', 'Desert', 'Tropical', 'Nautical', 'City'],
  'floral-and-botanical': ['Floral', 'Botanical'],
  metallic: ['Metallic'],
  modern: ['Modern', 'Geometric', 'Minimal', 'Black & White', 'Typographic'],
  photo: ['Photo'],
  rustic: ['Rustic', 'Country', 'Mountains', 'Forest'],
  seasonal: ['Seasonal', 'Summer', 'Winter', 'Fall', 'Spring'],
  textured: ['Textured', 'Watercolor', 'Paper', 'Marble', 'Paint'],
};

const slugsByTags = new Map<string, string[]>();
for (const slug in eCardCategorySlugsToTagsMap) {
  if (!Object.prototype.hasOwnProperty.call(eCardCategorySlugsToTagsMap, slug)) {
    continue;
  }
  eCardCategorySlugsToTagsMap[slug]?.forEach((tag) => {
    if (!slugsByTags.has(tag)) {
      slugsByTags.set(tag, []);
    }
    slugsByTags.get(tag)!.push(slug);
  });
}

export const getECardSlugsByTags = (categoryTags: string[]) => {
  const tagsToSlugs = categoryTags.reduce((acc, tag) => {
    const slugs = slugsByTags.get(tag);
    if (slugs) {
      acc.push(...slugs);
    }
    return acc;
  }, [] as string[]);
  return Array.from(new Set(tagsToSlugs));
};

export const eCardLayouts: SelectFilterOptions = [
  {
    name: 'Single Page',
    value: 'aloha',
  },
  {
    name: 'Multi Page',
    value: 'brannan',
  },
];

export const eCardCollections: SelectFilterOptions = [
  {
    name: 'Joy',
    value: 'joy',
  },
  {
    name: 'Rachel Zoe',
    value: 'rachelzoe',
    slug: 'rachel zoe',
    tag: 'New',
  },
  {
    name: 'Paper Culture',
    value: 'paper culture',
  },
  {
    name: 'Paperlust',
    value: 'paperlust',
  },
];

export const eCardCategories = [
  {
    name: 'Bold',
    value: 'bold',
  },
  {
    name: 'Classic',
    value: 'classic',
  },
  {
    name: 'Cultural',
    value: 'cultural',
  },
  {
    name: 'Destination',
    value: 'destination',
  },
  {
    name: 'Floral & Botanical',
    value: 'floral-and-botanical',
  },
  {
    name: 'Metallic',
    value: 'metallic',
  },
  {
    name: 'Modern',
    value: 'modern',
  },
  {
    name: 'Photo',
    value: 'photo',
  },
  {
    name: 'Rustic',
    value: 'rustic',
  },
  {
    name: 'Seasonal',
    value: 'seasonal',
  },
  {
    name: 'Textured',
    value: 'textured',
  },
] as const;

export const eCardColors: SelectFilterOptions = [
  {
    name: 'Red',
    value: '#8A1F1F',
  },
  {
    name: 'Orange',
    value: '#F09E62',
  },
  {
    name: 'Yellow',
    value: '#FFE798',
  },
  {
    name: 'Green',
    value: '#627154',
  },
  {
    name: 'Blue',
    value: '#416497',
  },
  {
    name: 'Brown',
    value: '#796051',
  },
  {
    name: 'Purple',
    value: '#866D8E',
  },
  {
    name: 'Pink',
    value: '#F8DBDA',
  },
  {
    name: 'Beige',
    value: '#F0E8E2',
  },
  {
    name: 'Gray',
    value: '#E0E0E0',
  },
  {
    name: 'Black',
    value: '#333333',
  },
  {
    name: 'White',
    value: '#FFFFFF',
  },
  {
    name: 'Gold',
    value: 'linear-gradient(135deg, #8F5303 0%, #E4C269 28.65%, #FFE68A 42.71%, #F0D16F 55.21%, #BD8A1C 75%, #C59320 84.37%, #AB7613 100%)',
  },
  {
    name: 'Rose Gold',
    value:
      'linear-gradient(135deg, #89392D 0%, #DF9C7A 28.65%, #FFCBAF 42.71%, #E5A280 55.21%, #A65C47 72.92%, #9B4F3E 84.36%, #87392F 100%)',
  },
  {
    name: 'Silver',
    value: 'linear-gradient(135deg, #585550 0%, #908F8B 28.65%, #E5E5E5 42.71%, #BEBDBB 55.21%, #6C6965 75%, #6E6B66 84.37%, #585550 100%)',
  },
];

export const eCardPrints: SelectFilterOptions = [
  {
    name: 'Show art with prints available',
    value: 'print',
  },
];

export const getCollectionSlug = (value: string) => {
  return eCardCollections.find((collection) => collection.value === value)?.slug ?? value;
};

export const invitationDesignsCanonicalUrl = `https://withjoy.com/wedding-invitations/`;
export const saveTheDateDesignsCanonicalUrl = `https://withjoy.com/save-the-date/`;
export const designsCanonicalUrl = `https://withjoy.com/designs/`;

export const getUseTemplateCtaId = (productType: string, themeId: string) => {
  return snakeToCamel(`use-${slugify(productType)}-template-${slugify(themeId)}`);
};

export const eCardCategorySlugs = eCardCategories.map((category) => category.value);

export type ECardCategorySlug = (typeof eCardCategorySlugs)[number];

export const getCategoryLabel = (slug: ECardCategorySlug) => eCardCategories.find(({ value }) => value === slug)?.name || '';
